(() => {
  const pendingChanges = new WeakMap();

  function getLineKey(lineItem) {
    return lineItem.querySelector('[data-line-key]')?.getAttribute('data-line-key');
  }

  function getSectionId(element) {
    return element?.closest('.shopify-section')?.id.replace('shopify-section-', '');
  }

  function replaceLineItem(currentItem, updatedItem) {
    const activeElement = document.activeElement;
    const shouldRestoreFocus = currentItem.contains(activeElement);
    const focusedButtonIndex = shouldRestoreFocus
      ? Array.from(currentItem.querySelectorAll('.quantity-selector__button')).indexOf(activeElement)
      : -1;

    currentItem.replaceWith(updatedItem);

    if (focusedButtonIndex >= 0) {
      requestAnimationFrame(() => {
        updatedItem.querySelectorAll('.quantity-selector__button')[focusedButtonIndex]?.focus({ preventScroll: true });
      });
    }
  }

  function replaceCartRow(currentRow, updatedRow) {
    const activeElement = document.activeElement;
    const shouldRestoreFocus = currentRow.contains(activeElement);
    const focusedButtonIndex = shouldRestoreFocus
      ? Array.from(currentRow.querySelectorAll('.cart-quantity-selector .quantity-selector__button')).indexOf(activeElement)
      : -1;

    currentRow.replaceWith(updatedRow);

    if (focusedButtonIndex >= 0) {
      requestAnimationFrame(() => {
        updatedRow
          .querySelectorAll('.cart-quantity-selector .quantity-selector__button')
          [focusedButtonIndex]?.focus({ preventScroll: true });
      });
    }
  }

  function updateCartPageWithoutRefresh(mainCartSection, sectionHtml) {
    const updatedDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const updatedContent = updatedDocument.querySelector('.container');

    if (!updatedContent) return false;

    const updatedRows = Array.from(updatedContent.querySelectorAll('.order-summary__body > tr'));

    if (updatedRows.length === 0) {
      mainCartSection.replaceChildren(...updatedDocument.body.childNodes);
      return true;
    }

    const currentRows = new Map(
      Array.from(mainCartSection.querySelectorAll('.order-summary__body > tr'))
        .map((row) => [getLineKey(row), row])
        .filter(([lineKey]) => lineKey)
    );

    updatedRows.forEach((updatedRow) => {
      const lineKey = getLineKey(updatedRow);
      const currentRow = currentRows.get(lineKey);

      if (currentRow) {
        replaceCartRow(currentRow, updatedRow);
        currentRows.delete(lineKey);
      } else {
        mainCartSection.querySelector('.order-summary__body')?.append(updatedRow);
      }
    });

    currentRows.forEach((row) => {
      const animation = row.animate(
        [
          { opacity: 1 },
          { opacity: 0 },
        ],
        { duration: 150, easing: 'ease-out' }
      );
      animation.finished.then(() => row.remove());
    });

    const currentTotals = mainCartSection.querySelector('.cart-form__totals');
    const updatedTotals = updatedContent.querySelector('.cart-form__totals');

    if (currentTotals && updatedTotals) {
      currentTotals.replaceWith(updatedTotals);
    }

    return true;
  }

  async function changeCartPageQuantity(control, lineKey, targetQuantity) {
    const mainCartSection = control.closest('.shopify-section--main-cart');
    const sectionId = getSectionId(mainCartSection);

    if (!mainCartSection || !sectionId) return;

    const input = control.querySelector('[data-line-key]');
    const previousQuantity = Number(input?.defaultValue || input?.value || 1);
    const loader = control.closest('tr')?.querySelector('pill-loader') || control.closest('line-item')?.querySelector('pill-loader');
    const buttons = Array.from(control.querySelectorAll('.quantity-selector__button'));
    const sections = [sectionId];

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections },
      })
    );

    const uniqueSections = [...new Set(sections)];
    loader?.setAttribute('aria-busy', 'true');
    buttons.forEach((button) => button.setAttribute('disabled', 'disabled'));

    try {
      const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: lineKey,
          quantity: targetQuantity,
          sections: uniqueSections,
        }),
      });

      const cart = await response.json();

      if (!response.ok) {
        throw new Error(cart.description || 'Não foi possível atualizar o carrinho.');
      }

      const updated = updateCartPageWithoutRefresh(mainCartSection, cart.sections?.[sectionId]);

      if (!updated) {
        throw new Error('A Shopify não retornou a seção atualizada do carrinho.');
      }

      enhanceCartPageQuantityControls();

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'line-item:change',
            cart,
          },
        })
      );
    } catch (error) {
      if (input) input.value = String(previousQuantity);
      loader?.removeAttribute('aria-busy');
      buttons.forEach((button) => button.removeAttribute('disabled'));
      console.error('[cart-quantity-selector]', error);
    }
  }

  function enhanceCartPageQuantityControls() {
    if (window.themeVariables?.settings?.pageType !== 'cart') return;

    document.querySelectorAll('.shopify-section--main-cart line-item-quantity').forEach((control) => {
      if (control.__ajaxQuantityUpdate) return;

      control.removeEventListener('change', control._onQuantityChanged);
      control.removeEventListener('click', control._onRemoveLinkClicked);

      control.addEventListener('change', (event) => {
        if (!event.target.hasAttribute('data-line-key')) return;

        changeCartPageQuantity(
          control,
          event.target.getAttribute('data-line-key'),
          Number.parseInt(event.target.value, 10)
        );
      });

      control.addEventListener('click', (event) => {
        const removeLink = event.target.closest('a[href*="/cart/change"]');

        if (!removeLink) return;

        event.preventDefault();
        const url = new URL(removeLink.href);
        changeCartPageQuantity(control, url.searchParams.get('id'), Number.parseInt(url.searchParams.get('quantity'), 10));
      });

      control.__ajaxQuantityUpdate = true;
    });
  }

  function updateDrawerWithoutFlash(drawer, event, originalHandler) {
    if (event.detail?.baseEvent !== 'line-item:change' || event.detail.cart?.item_count === 0) {
      return originalHandler(event);
    }

    const sectionId = getSectionId(drawer);
    const sectionHtml = event.detail.cart?.sections?.[sectionId];

    if (!sectionHtml) {
      return originalHandler(event);
    }

    const updatedDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const updatedDrawer = updatedDocument.querySelector('#cart-drawer');

    if (!updatedDrawer) {
      return originalHandler(event);
    }

    const currentItems = new Map(
      Array.from(drawer.querySelectorAll('.cart-drawer__line-items > line-item'))
        .map((lineItem) => [getLineKey(lineItem), lineItem])
        .filter(([lineKey]) => lineKey)
    );

    const updatedItems = Array.from(updatedDrawer.querySelectorAll('.cart-drawer__line-items > line-item'));

    updatedItems.forEach((updatedItem) => {
      const lineKey = getLineKey(updatedItem);
      const currentItem = currentItems.get(lineKey);

      if (currentItem) {
        replaceLineItem(currentItem, updatedItem);
        currentItems.delete(lineKey);
      } else {
        drawer.querySelector('.cart-drawer__line-items')?.append(updatedItem);
      }
    });

    const currentRecommendations = drawer.querySelector('.cart-drawer__recommendations');
    const updatedRecommendations = updatedDrawer.querySelector('.cart-drawer__recommendations');

    if (currentRecommendations && updatedRecommendations) {
      currentRecommendations.replaceWith(updatedRecommendations);
    } else if (currentRecommendations && !updatedRecommendations) {
      currentRecommendations.remove();
    } else if (!currentRecommendations && updatedRecommendations) {
      drawer.querySelector('.cart-drawer__line-items')?.after(updatedRecommendations);
    }

    const currentFooter = drawer.querySelector('[slot="footer"]');
    const updatedFooter = updatedDrawer.querySelector('[slot="footer"]');

    if (currentFooter && updatedFooter) {
      currentFooter.replaceChildren(...updatedFooter.childNodes);
    }
  }

  customElements.whenDefined('cart-drawer').then(() => {
    const drawer = document.getElementById('cart-drawer');

    if (!drawer?._onCartChangedListener || drawer.__smoothQuantityUpdate) return;

    const originalHandler = drawer._onCartChangedListener;
    const smoothHandler = (event) => updateDrawerWithoutFlash(drawer, event, originalHandler);

    document.removeEventListener('cart:change', originalHandler);
    document.addEventListener('cart:change', smoothHandler);

    drawer._onCartChangedListener = smoothHandler;
    drawer.__smoothQuantityUpdate = true;
  });

  customElements.whenDefined('line-item-quantity').then(enhanceCartPageQuantityControls);

  document.addEventListener('click', (event) => {
    const button = event.target.closest('.cart-quantity-selector .quantity-selector__button');

    if (!button) return;

    const control = button.closest('line-item-quantity');
    const input = control?.querySelector('[data-line-key]');

    if (!control || !input) return;

    window.clearTimeout(pendingChanges.get(control));
    pendingChanges.set(
      control,
      window.setTimeout(() => {
        input.dispatchEvent(new Event('change', { bubbles: true }));
        pendingChanges.delete(control);
      }, 250)
    );
  });
})();
